import React from 'react';
import { useStringCombobox, StringComboboxConfig } from '../../hooks/useStringCombobox';
import { StringComboboxLabels, textDirection } from '../../utils/stringComboboxUtils';
import styles from '../../pages/ExpenseEditorPage.module.css';

interface StringComboboxProps {
  value: string;
  onChange: (value: string) => void;
  config: StringComboboxConfig;
  labels: StringComboboxLabels;
  invalid?: boolean;
  nameInputClassName?: string;
  arrowClassName?: string;
  disabled?: boolean;
}

const StringCombobox: React.FC<StringComboboxProps> = ({
  value,
  onChange,
  config,
  labels,
  invalid = false,
  nameInputClassName = '',
  arrowClassName = '',
  disabled = false,
}) => {
  const {
    inputContainerRef,
    nameInputRef,
    optionRefs,
    fieldTitle,
    showSuggestions,
    searchQuery,
    isLoading,
    filteredItems,
    highlightedIndex,
    setHighlightedIndex,
    openSuggestions,
    closeSuggestions,
    selectItem,
    removeItem,
    handleSearchKeyDown,
    handleNameChange,
  } = useStringCombobox(value, onChange, config);

  return (
    <div
      className={`${styles.vendorInputContainer} ${showSuggestions ? styles.vendorInputOpen : ''}`.trim()}
      ref={inputContainerRef}
    >
      <div className={styles.vendorFieldRow}>
        <button
          type="button"
          className={`${styles.vendorArrowButton} ${arrowClassName}`.trim()}
          onClick={() => {
            if (disabled) return;
            if (showSuggestions) {
              closeSuggestions();
            } else {
              void openSuggestions();
            }
          }}
          aria-expanded={showSuggestions}
          aria-haspopup="listbox"
          aria-label={labels.openListAriaLabel}
          tabIndex={disabled ? -1 : 0}
          disabled={disabled}
        />
        <div className={styles.vendorNameInputWrap} title={fieldTitle}>
          <input
            ref={nameInputRef}
            type="text"
            dir={textDirection(value)}
            className={`${styles.vendorNameInput} ${textDirection(value) === 'ltr' ? styles.vendorNameInputLtr : ''} ${invalid ? styles.invalidField : ''} ${nameInputClassName}`.trim()}
            value={value}
            onChange={(e) => handleNameChange(e.target.value)}
            aria-label={labels.fieldLabel}
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            aria-controls={showSuggestions ? `${labels.optionIdPrefix}-list` : undefined}
            aria-activedescendant={
              showSuggestions && highlightedIndex >= 0
                ? `${labels.optionIdPrefix}-${highlightedIndex}`
                : undefined
            }
            disabled={disabled}
            onFocus={() => {
              if (!disabled && !showSuggestions) {
                void openSuggestions();
              }
            }}
            onBlur={(e) => {
              if (textDirection(e.target.value) === 'ltr') {
                e.target.scrollLeft = 0;
              }
            }}
            onKeyDown={(e) => {
              if (!showSuggestions && e.key === 'ArrowDown' && !disabled) {
                e.preventDefault();
                void openSuggestions();
                return;
              }
              if (showSuggestions) {
                handleSearchKeyDown(e);
              }
            }}
          />
        </div>
      </div>

      {showSuggestions && !disabled && (
        <div
          className={styles.vendorSuggestions}
          role="listbox"
          id={`${labels.optionIdPrefix}-list`}
          aria-label={labels.fieldLabel}
        >
          <div className={styles.vendorComboboxList}>
            {isLoading ? (
              <div className={styles.vendorSuggestion}>
                <div className={styles.loadingSpinner} />
                טוען הצעות...
              </div>
            ) : filteredItems.length === 0 ? (
              <div className={styles.vendorSuggestion}>
                {searchQuery.trim() ? labels.noResultsText : labels.emptyText}
              </div>
            ) : (
              filteredItems.map((item, index) => (
                <button
                  key={item}
                  id={`${labels.optionIdPrefix}-${index}`}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  type="button"
                  className={`${styles.vendorComboboxOption} ${value === item ? styles.vendorComboboxOptionActive : ''} ${index === highlightedIndex ? styles.vendorComboboxOptionHighlighted : ''}`.trim()}
                  role="option"
                  aria-selected={value === item}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectItem(item);
                  }}
                >
                  <span
                    className={`${styles.vendorName} ${textDirection(item) === 'ltr' ? styles.vendorNameLtr : ''}`.trim()}
                    dir={textDirection(item)}
                    title={item}
                  >
                    {item}
                  </span>
                  <span
                    className={styles.removeVendorButton}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeItem(item);
                    }}
                    title={labels.removeItemTitle}
                  >
                    ×
                  </span>
                </button>
              ))
            )}
          </div>
          {searchQuery.trim() &&
            !filteredItems.some((item) => item.toLowerCase() === searchQuery.trim().toLowerCase()) && (
              <button
                type="button"
                className={styles.vendorComboboxUseTyped}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectItem(searchQuery.trim());
                }}
              >
                השתמש ב־“{searchQuery.trim()}”
              </button>
            )}
        </div>
      )}
    </div>
  );
};

export default StringCombobox;
